import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { OnvifCredential, UnitScanCreateResponse } from "./onvifScanInterfaces";
import { useOnvifScanStore } from "../onvifScanStore/onvifScanStore";
import { buildUnitScanRequestBody } from "./cameraUnitScan";
import { pollCameraUnitScan } from "./pollCameraUnitScan";

interface StartCameraUnitScanArgs {
  siteId: string | number;
  credentials: OnvifCredential[];
  cameraIds?: number[];
  navigate: NavigateFunction;
}

/**
 * Kick off a camera unit scan (POST camera-unit-scan/) for every camera on the
 * site that has a host, then poll for the result. No WireGuard tunnel is
 * involved: Camera Admin reaches the units over whatever route the site has
 * (IPsec VPN for Genetec direct sites).
 *
 * The 202 body says how many cameras are targeted and which logins will be
 * tried (username + kind, never a password); both are kept for display while
 * the scan runs.
 */
export const startCameraUnitScan = ({
  siteId,
  credentials,
  cameraIds,
  navigate,
}: StartCameraUnitScanArgs) => {
  const {
    setScanStatus,
    setTaskId,
    setErrorMessage,
    setUnitScanResult,
    setUnitSelectedIds,
    setUnitScanTargets,
    setUnitScanCredentialRefs,
    setApplyResult,
  } = useOnvifScanStore.getState();

  setUnitScanResult(null);
  setUnitSelectedIds({});
  setApplyResult(null);
  setUnitScanTargets(null);
  setUnitScanCredentialRefs([]);
  setErrorMessage("");
  setTaskId(null);
  setScanStatus("pending");

  const AxiosUI = createApi("");
  const body = buildUnitScanRequestBody({
    customerId: siteId,
    cameraIds,
    credentials,
  });

  const retryFunction = () =>
    startCameraUnitScan({ siteId, credentials, cameraIds, navigate });

  AxiosUI.post("camera-unit-scan/", body).then(
    (response: AxiosResponse<UnitScanCreateResponse>) => {
      const taskId = response.data?.task_id;
      if (!taskId) {
        setErrorMessage("Scan did not return a task id.");
        setScanStatus("failed");
        return;
      }
      setTaskId(taskId);
      setUnitScanTargets(response.data?.targets ?? null);
      setUnitScanCredentialRefs(response.data?.credentials ?? []);
      pollCameraUnitScan(taskId, Date.now());
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        const data = reason.response?.data as { error?: string } | undefined;
        setErrorMessage(
          data?.error || "Failed to start the scan. Please try again."
        );
        setScanStatus("failed");
      });
    }
  );
};
